import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  Operation,
  NextLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import retry from "promise-retry";

import { cache } from "./cache";
import { AuthService } from "../../components/Auth";

export interface GraphQlClientOptions {
  authService: AuthService;
}

export class GraphQlClient {
  private readonly cache: InMemoryCache;
  private readonly apolloClient: ApolloClient<any>;
  private readonly authService: AuthService;

  constructor({ authService }: GraphQlClientOptions) {
    this.cache = cache;
    this.authService = authService;

    const httpLink = createHttpLink({
      uri: "/api/graphql",
    });

    const authBlockLink = new ApolloLink(
      (operation: Operation, forward: NextLink) => {
        if (!this.authService.hasToken()) {
          console.warn(
            "Blocked GraphQL request due to missing authentication:",
            operation.operationName,
          );
          return null;
        }
        return forward(operation);
      },
    );

    const authLink = setContext(async (_, { headers }) => {
      const authHeaders = await authService.getAuthHeaders();
      return {
        headers: {
          ...headers,
          ...authHeaders,
        },
      };
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          );
        });
      }
      if (networkError) {
        console.error(`[Network error]: ${networkError}`);
        authService.handleAnyAuthenticationErrors(networkError);
      }
    });

    this.apolloClient = new ApolloClient({
      link: errorLink.concat(authBlockLink).concat(authLink).concat(httpLink),
      cache: this.cache,
    });

    authService.onSignIn(() => this.resetStore());
    authService.onSignOut(() => this.resetStore());
  }

  getApolloClient(): ApolloClient<any> {
    return this.apolloClient;
  }

  private async resetStore() {
    await retry(
      (tryAgain) =>
        this.apolloClient.resetStore().catch((error) => tryAgain(error)),
      {
        minTimeout: 100,
        maxTimeout: 200,
        maxRetryTime: 3000,
      },
    );
  }
}
